/**
 * "Complete the look" strip of the product page (snippets/complete-the-look.liquid).
 *
 * The "+" of a card opens its size picker over the image (one picker open at a time); picking a size,
 * or the "+" of a single-size product, adds it through the addToCart helper of custom.js, which also
 * opens the added-to-cart popup. The picker closes from its "Select size" footer, Escape or a click
 * outside the card.
 *
 * Also used by the mobile strip of the look pages (component-look.js), which drives it from outside:
 * - openPicker(card) opens a card's picker
 * - every pick fires a cancelable "complete-look:select" event ({ variantId, card }); cancelling it keeps
 *   the size for the caller (the "Add full look" flow) instead of adding it to the cart
 */
class CompleteTheLook extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.addEventListener('click', this.onClick.bind(this), { signal });
    document.addEventListener('click', this.onDocumentClick.bind(this), { signal });
    document.addEventListener('keydown', this.onKeydown.bind(this), { signal });
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  get openCard() {
    return this.querySelector('.complete-look__card--picking');
  }

  onClick(event) {
    const actionElement = event.target.closest('[data-action]');

    if (!actionElement) {
      return;
    }

    const card = actionElement.closest('[data-complete-look-card]');

    switch (actionElement.dataset.action) {
      case 'toggle':
        this.setPickerState(card, card !== this.openCard);
        break;

      case 'close':
        this.setPickerState(card, false);
        card.querySelector('[data-action="toggle"]')?.focus();
        break;

      case 'add':
        this.addVariant(actionElement, card);
        break;
    }
  }

  onDocumentClick(event) {
    const openCard = this.openCard;

    if (openCard && !openCard.contains(event.target)) {
      this.setPickerState(openCard, false);
    }
  }

  onKeydown(event) {
    const openCard = this.openCard;

    if (event.key === 'Escape' && openCard) {
      this.setPickerState(openCard, false);
      openCard.querySelector('[data-action="toggle"]')?.focus();
    }
  }

  setPickerState(card, isOpen) {
    if (isOpen && this.openCard) {
      this.setPickerState(this.openCard, false);
    }

    const picker = card.querySelector('.complete-look__picker');
    const toggle = card.querySelector('[data-action="toggle"]');

    card.classList.toggle('complete-look__card--picking', isOpen);

    if (picker) {
      picker.hidden = !isOpen;
    }

    if (toggle) {
      toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }

    if (isOpen) {
      picker?.querySelector('.complete-look__size-button:not([disabled])')?.focus({ preventScroll: true });
    }
  }

  openPicker(card) {
    this.setPickerState(card, true);
  }

  async addVariant(button, card) {
    const variantId = button.dataset.variantId;

    if (!variantId || button.hasAttribute('aria-busy') || typeof addToCart !== 'function') {
      return;
    }

    const selectEvent = new CustomEvent('complete-look:select', {
      bubbles: true,
      cancelable: true,
      detail: { variantId, card }
    });

    // Read defaultPrevented rather than the dispatchEvent() result: on touch devices a vendor script patches
    // dispatchEvent and it returns undefined
    this.dispatchEvent(selectEvent);

    if (selectEvent.defaultPrevented) {
      this.setPickerState(card, false);
      return;
    }

    button.setAttribute('aria-busy', 'true');

    try {
      await addToCart({ id: variantId, quantity: 1 }, true, false, false);
    } finally {
      button.removeAttribute('aria-busy');
      this.setPickerState(card, false);
    }
  }
}

if (!window.customElements.get('complete-the-look')) {
  window.customElements.define('complete-the-look', CompleteTheLook);
}
